import type React from 'react';
import type { InteractionPart } from '@lib/models/InteractionPart';
import type { Interaction } from '@lib/models/Interaction';

// Logical grouping for renderer dispatch. Multiple EventTypes can map to
// the same kind — notably, the three function event types share a single
// `'function'` renderer because they're the same part mutating through
// states. `'boxed'` is the catch-all for event types that today render
// through `MessageNonTextDisplay` (InlineData, FileData, code execution,
// etc.).
export type PartKind =
    | 'text'
    | 'thought'
    | 'function'
    | 'waiting'
    | 'suspended'
    | 'boxed'
    // UI render tools (server marks them with a `UI_` name prefix / later a
    // dedicated UIFunctionCall content type). First-class content: rendered by
    // a host-provided renderer and NOT gated by `showToolCalls`.
    | 'uiTool'
    // ask_user: a yes/no question rendered with Yes/No buttons inside the library
    // (built-in, not host-provided like uiTool). Routed by function name, not isUi.
    | 'askUser';

export interface PartRendererProps {
    part: InteractionPart;
    message: Interaction;
    onGoing: boolean;
    preview: boolean;
    isLastInlineInGroup?: boolean;
    friendlyMode?: boolean;
    onLinkClicked: (link: string, content: string | null) => void;
}

export type PartRenderer = React.ComponentType<PartRendererProps>;
export type PartRendererMap = Partial<Record<PartKind, PartRenderer>>;

/**
 * Match the ask_user tool by function name across connectors. The programmed
 * path emits the bare `ask_user`; live LLM connectors qualify it as
 * `{Plugin}-ask_user` / `{Plugin}_ask_user` (with an empty pluginName), so a
 * literal `=== 'ask_user'` misses real-model calls.
 */
export const isAskUser = (name?: string | null): boolean =>
    !!name && (name === 'ask_user' || name.endsWith('-ask_user') || name.endsWith('_ask_user'));
